var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
// Copyright 2022 The Parca Authors
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
import React, { useCallback, useEffect, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { Button, Table, useURLState } from '@parca/components';
import { getLastItem, isSearchMatch, parseParams, valueFormatter, } from '@parca/utilities';
import { hexifyAddress } from '../utils';
export var RowLabel = function (meta) {
    var _a, _b, _c, _d, _e, _f;
    if (meta === undefined)
        return '<unknown>';
    var mapping = "".concat(((_a = meta === null || meta === void 0 ? void 0 : meta.mapping) === null || _a === void 0 ? void 0 : _a.file) !== undefined && ((_b = meta === null || meta === void 0 ? void 0 : meta.mapping) === null || _b === void 0 ? void 0 : _b.file) !== ''
        ? "[".concat((_c = getLastItem(meta.mapping.file)) !== null && _c !== void 0 ? _c : '', "]")
        : '');
    if (((_d = meta.function) === null || _d === void 0 ? void 0 : _d.name) !== undefined && ((_e = meta.function) === null || _e === void 0 ? void 0 : _e.name) !== '')
        return "".concat(mapping, " ").concat(meta.function.name);
    var address = hexifyAddress((_f = meta.location) === null || _f === void 0 ? void 0 : _f.address);
    var fallback = "".concat(mapping, " ").concat(address);
    return fallback === '' ? '<unknown>' : fallback;
};
var columnHelper = createColumnHelper();
var addPlusSign = function (num) {
    if (num.charAt(0) === '0' || num.charAt(0) === '-') {
        return num;
    }
    return "+".concat(num);
};
export var TopTable = React.memo(function TopTable(_a) {
    var _b;
    var top = _a.data, unit = _a.sampleUnit, navigateTo = _a.navigateTo, loading = _a.loading, currentSearchString = _a.currentSearchString, setActionButtons = _a.setActionButtons;
    var router = parseParams(window === null || window === void 0 ? void 0 : window.location.search);
    var rawDashboardItems = useURLState({ param: 'dashboard_items' })[0];
    var rawcompareMode = useURLState({ param: 'compare_a' })[0];
    var compareMode = rawcompareMode === undefined ? false : rawcompareMode === 'true';
    var dashboardItems = useMemo(function () {
        if (rawDashboardItems !== undefined) {
            return rawDashboardItems;
        }
        return ['icicle'];
    }, [rawDashboardItems]);
    var columns = useMemo(function () {
        var cols = [
            columnHelper.accessor('meta', {
                header: function () { return _jsx("span", __assign({ className: "text-left" }, { children: "Name" })); },
                cell: function (info) {
                    var meta = info.row.original.meta;
                    var name = RowLabel(meta);
                    return name;
                },
                sortingFn: function (a, b) {
                    var aName = RowLabel(a.original.meta);
                    var bName = RowLabel(b.original.meta);
                    return aName.localeCompare(bName);
                },
            }),
            columnHelper.accessor('flat', {
                header: function () { return 'Flat'; },
                cell: function (info) { return valueFormatter(Number(info.getValue()), unit, 2); },
                size: 150,
                meta: {
                    align: 'right',
                },
                sortDescFirst: true,
            }),
            columnHelper.accessor('cumulative', {
                header: function () { return 'Cumulative'; },
                cell: function (info) { return valueFormatter(Number(info.getValue()), unit, 2); },
                size: 150,
                meta: {
                    align: 'right',
                },
                sortDescFirst: true,
            }),
        ];
        if (compareMode) {
            cols.push(columnHelper.accessor('diff', {
                header: function () { return 'Diff'; },
                cell: function (info) { return addPlusSign(valueFormatter(Number(info.getValue()), unit, 2)); },
                size: 150,
                meta: {
                    align: 'right',
                },
                sortDescFirst: true,
            }));
        }
        return cols;
    }, [unit, compareMode]);
    var selectSpan = useCallback(function (span) {
        if (navigateTo != null) {
            navigateTo('/', __assign(__assign({}, router), { search_string: span.trim() }), { replace: true });
        }
    }, [navigateTo, router]);
    var onRowClick = useCallback(function (row) {
        // If there is only one dashboard item, we don't want to select a span
        if (dashboardItems.length <= 1) {
            return;
        }
        var meta = row.meta;
        if (meta === undefined) {
            return;
        }
        var name = RowLabel(meta);
        selectSpan(name);
    }, [selectSpan, dashboardItems.length]);
    var shouldHighlightRow = useCallback(function (row) {
        var meta = row.meta;
        if (meta === undefined)
            return false;
        var name = RowLabel(meta);
        return isSearchMatch(currentSearchString, name);
    }, [currentSearchString]);
    var enableHighlighting = useMemo(function () {
        return currentSearchString != null && (currentSearchString === null || currentSearchString === void 0 ? void 0 : currentSearchString.length) > 0;
    }, [currentSearchString]);
    var clearSelection = useCallback(function () {
        if (navigateTo != null) {
            navigateTo('/', __assign(__assign({}, router), { search_string: '' }), { replace: true });
        }
    }, [navigateTo, router]);
    useEffect(function () {
        if (setActionButtons === undefined) {
            return;
        }
        setActionButtons(dashboardItems.length > 1 ? (_jsx(Button, __assign({ color: "neutral", onClick: clearSelection, className: "w-auto", variant: "neutral", disabled: currentSearchString === undefined || currentSearchString.length === 0 }, { children: "Clear selection" }))) : (_jsx(_Fragment, {})));
    }, [dashboardItems, clearSelection, currentSearchString, setActionButtons]);
    var initialSorting = useMemo(function () {
        return [{ id: compareMode ? 'diff' : 'cumulative', desc: true }];
    }, [compareMode]);
    var total = top != null ? top.list.length : 0;
    if (total === 0 && !loading)
        return _jsx(_Fragment, { children: "Profile has no samples" });
    return (_jsx("div", __assign({ className: "relative" }, { children: _jsx("div", __assign({ className: "w-full font-robotoMono h-[80vh] overflow-scroll" }, { children: _jsx(Table, { data: (_b = top === null || top === void 0 ? void 0 : top.list) !== null && _b !== void 0 ? _b : [], columns: columns, initialSorting: initialSorting, onRowClick: onRowClick, enableHighlighting: enableHighlighting, shouldHighlightRow: shouldHighlightRow, usePointerCursor: dashboardItems.length > 1 }) })) })));
});
export default TopTable;
